import { Entity } from './base-raptor-models';
import { InspectionItemBinding } from './inspection-item-binding.enum';
import { InspectionItemType } from './inspection-item-type.enum';
import { NotesInputType } from './notes-input-type.enum';
import { NumericRangeConstraints } from './numeric-range-constraints.model';

export enum InspectionTemplateUsage {
    Standard = 'Standard',
    CheckIn = 'CheckIn',
    Handback = 'Handback',
}

/**
 * Defines whether an inspection template is scoped to a specific branch or globally available to all branches within a tenant
 */
export enum InspectionTemplateScope {
    Branch = 'Branch',
    Global = 'Global',
}

/**
 * Approval state of a persisted note option.
 * Approved options are selectable by technicians; PendingReview options were authored
 * freehand by a tech at inspection-save time and are awaiting admin approval.
 */
export type NoteOptionStatus = 'Approved' | 'PendingReview';

/**
 * Hard limits enforced by the persisted note-options catalog.
 * Mirrored from the backend MaxLength attributes; clients must enforce these BEFORE Save
 * so the admin sees inline validation rather than a server-side 400.
 */
export const NOTE_OPTION_NOTE_MAX_LENGTH = 250;
export const NOTE_OPTION_RECOMMENDATION_MAX_LENGTH = 250;
export const NOTE_OPTION_MAX_RECOMMENDATIONS_PER_NOTE = 10;
export const NOTE_OPTION_MAX_OPTIONS_PER_ITEM = 50;

/**
 * Persisted, admin-curated note option attached to a specific inspection template item.
 * Replaces the legacy untyped notes/recommendations history strings.
 *
 * The optional `_staged` flags are client-side staging hints used by the catalog modal to
 * track approve / edit / new-on-save intent so the parent template editor can render
 * status badges before the single PUT round-trip commits them. They are stripped on Save.
 */
export interface NoteOption extends Entity {
    /** Composite-FK component back to the parent InspectionTemplateItem. Required by the API
     * on save; new options created in the catalog modal carry the parent item's id. */
    inspectionTemplateItemId: string | null;
    note: string;
    recommendations: string[];
    status: NoteOptionStatus;
    isDeleted: boolean;
    order: number;
    approvedBy?: string | null;
    approvedDate?: string | null;
    /** Client-side staging hints; cleared on the next successful Save. */
    _staged?: NoteOptionStaged;
}

/**
 * Client-side staging flags for a NoteOption pending the next template Save.
 * Persisted on the form model only; never sent to or returned from the API.
 */
export interface NoteOptionStaged {
    approving?: boolean;
    edited?: boolean;
    isNew?: boolean;
    /** Client-only identity for unsaved rows so the modal can target a specific row in its
     * edit state before the server has assigned a real id. Stripped on Save. */
    clientKey?: string;
}

/**
 * Inspection template entity
 * Defines the structure and configuration for inspections
 */
export interface InspectionTemplate {
    id: string;
    branchId: string;
    tenantId: string;
    createdDate?: Date;
    createdBy?: string;
    name?: string;
    defaultAllItemsToPass?: boolean;
    useAISuggestion?: boolean;
    useAIKnowledgeTipsOnWarning?: boolean;
    useAIKnowledgeTipsOnFail?: boolean;
    includeWarningsOnTransfer?: boolean;
    requireNotesOnFail?: boolean;
    /**
     * When true, the technician must sign on the tablet before a handback inspection can be transferred.
     * Skipped inspections are exempt.
     */
    requireTechnicianSignature?: boolean;
    requireNotesOnWarning?: boolean;
    requirePhotoOrVideoOnFail?: boolean;
    requirePhotoOrVideoOnWarning?: boolean;
    /**
     * /: when true, technicians at inspection time may pick only
     * options from the per-item catalog. Default false preserves freehand + AI behavior.
     */
    restrictTechAddNoteOptions?: boolean;
    usage: InspectionTemplateUsage;
    scope?: InspectionTemplateScope;
    sections: InspectionTemplateSection[];
}

/**
 * Section within an inspection template
 * Groups related inspection items together
 * Supports nested sections (max 2 levels deep)
 */
export interface InspectionTemplateSection {
    id: string | null;
    branchId: string | null;
    tenantId: string | null;
    inspectionTemplateId: string | null;
    name: string;
    /** Optional JobCategory this section maps to for service-work classification */
    jobCategoryId?: string | null;
    /**
     * When true every failed item in the section transfers as one job named after the section
     * instead of one job per item. Absent means false.
     */
    groupFindingsOnTransfer?: boolean;
    items: InspectionTemplateItem[];
    order: number;
    parentSectionId?: string | null;
    childSections: InspectionTemplateSection[];
}

/**
 * Base interface for all inspection template items
 * Contains common properties shared across all item types
 */
export interface InspectionTemplateItemBase {
    id: string | null;
    branchId: string | null;
    tenantId: string | null;
    sectionTemplateId: string | null;
    name: string;
    isRequired: boolean;
    isPhotoRequired: boolean;
    isVideoRequired: boolean;
    order: number;
    itemType: InspectionItemType;
    /** Optional JobCategory this item maps to for service-work classification */
    jobCategoryId?: string | null;
    /**
     * Write-back target for this item's entered value, applied on inspection transfer.
     * Must be compatible with itemType -- the API rejects a mismatched pair on save.
     */
    binding?: InspectionItemBinding;
    /**
     * Persisted, admin-curated note-options catalog for this item.
     * Carried inbound on PUT and outbound on GET. Optional so existing callsites that
     * never read or write the catalog continue to type-check.
     */
    noteOptions?: NoteOption[];
}

/**
 * Traffic light inspection template item
 * Allows pass/warning/fail assessment with configurable options
 */
export interface TrafficLightInspectionTemplateItem extends InspectionTemplateItemBase {
    itemType: InspectionItemType.TrafficLight;
    allowPass: boolean;
    allowWarning: boolean;
    allowFail: boolean;
    passLabel?: string;
    warningLabel?: string;
    failLabel?: string;
}

/**
 * Numeric range inspection template item
 * Captures numeric measurements with min/max thresholds
 * Supports configurable Pass/Warning/Fail constraints
 */
export interface NumericRangeInspectionTemplateItem extends InspectionTemplateItemBase {
    itemType: InspectionItemType.NumericRange;
    minValue: number;
    maxValue: number;
    unit: string;
    decimalPlaces: number;
    constraints?: NumericRangeConstraints | null;
    passLabel?: string;
    warningLabel?: string;
    failLabel?: string;
}

/**
 * Checkbox inspection template item
 * Simple true/false checkbox with custom labels
 */
export interface CheckboxInspectionTemplateItem extends InspectionTemplateItemBase {
    itemType: InspectionItemType.Checkbox;
    trueLabel: string;
    falseLabel: string;
}

/**
 * Notes inspection template item
 * Free-form rich text notes field with WYSIWYG editor
 */
export interface NotesInspectionTemplateItem extends InspectionTemplateItemBase {
    itemType: InspectionItemType.Notes;
    placeholder?: string;
    maxLength?: number;
    inputType: NotesInputType;
}

/**
 * DateInput inspection template item
 * Captures date values with optional min/max constraints
 */
export interface DateInputInspectionTemplateItem extends InspectionTemplateItemBase {
    itemType: InspectionItemType.DateInput;
    placeholder?: string;
    minDate?: string;
    maxDate?: string;
}

/**
 * TriState inspection template item
 * Positive / not-applicable / negative answer with custom labels
 */
export interface TriStateInspectionTemplateItem extends InspectionTemplateItemBase {
    itemType: InspectionItemType.TriState;
    positiveLabel: string;
    middleLabel: string;
    negativeLabel: string;
}

/**
 * Union type for all inspection template item types
 * Enables type-safe discrimination based on itemType property
 */
export type InspectionTemplateItem =
    | TrafficLightInspectionTemplateItem
    | NumericRangeInspectionTemplateItem
    | CheckboxInspectionTemplateItem
    | NotesInspectionTemplateItem
    | DateInputInspectionTemplateItem
    | TriStateInspectionTemplateItem;
